interface MarginValues {
    top: string | null;
    right: string | null;
    bottom: string | null;
    left: string | null;
}

interface StyleDeclarationReader {
    getPropertyValue(property: string): string;
}

interface PageDeclaration {
    pageName: string;
    sizeString: string;
    widthMm: number;
    heightMm: number;
    orientation: 'portrait' | 'landscape';
    pageMargins: MarginValues;
    containerMargins: MarginValues;
    hasExplicitMargins: boolean;
}

export interface PrintCssomData {
    portraitWidthPx: number;
    landscapeWidthPx: number;
}

const MM_TO_CSS_PX = 96 / 25.4; // CSS px per mm at 96 dpi
const EMPTY_MARGINS: MarginValues = { top: null, right: null, bottom: null, left: null };

// Chrome renders print at 144dpi (empirically verified), not the CSS-standard 96dpi.
// Margins are in CSS px (96dpi) so convert everything to inches before multiplying by PRINT_DPI.
const PRINT_DPI = 144;
const SCREEN_DPI = 96;
function resolveEffectivePrintWidthPx(page: PageDeclaration): number {
    const pm = page.hasExplicitMargins
        ? page.pageMargins
        : { top: '60px', right: '0px', bottom: '80px', left: '0px' };
    const cm = page.containerMargins;
    const totalH = (parseLengthToPx(pm.left) ?? 0) + (parseLengthToPx(pm.right) ?? 0)
                 + (parseLengthToPx(cm.left) ?? 0) + (parseLengthToPx(cm.right) ?? 0);
    return Math.round(PRINT_DPI * (page.widthMm / 25.4 - totalH / SCREEN_DPI));
}

function parseMarginFromStyle(style: StyleDeclarationReader): MarginValues {
    // CSSOM usually expands `margin` shorthand into longhands; read longhands first so mixed
    // longhand/shorthand declarations and later side-specific overrides are handled uniformly.
    const top = style.getPropertyValue('margin-top').trim() || null;
    const right = style.getPropertyValue('margin-right').trim() || null;
    const bottom = style.getPropertyValue('margin-bottom').trim() || null;
    const left = style.getPropertyValue('margin-left').trim() || null;

    if (top !== null || right !== null || bottom !== null || left !== null) {
        return { top, right, bottom, left };
    }

    // Some CSSOM implementations keep shorthand only. Support all CSS margin forms:
    // one value, vertical/horizontal, top/horizontal/bottom, or four explicit sides.
    const shorthand = style.getPropertyValue('margin').trim();
    if (!shorthand) return EMPTY_MARGINS;
    return parseMarginShorthand(shorthand);
}

function parseMarginShorthand(shorthand: string): MarginValues {
    const parts = shorthand.split(/\s+/).filter(Boolean);
    switch (parts.length) {
        case 1: return { top: parts[0], right: parts[0], bottom: parts[0], left: parts[0] };
        case 2: return { top: parts[0], right: parts[1], bottom: parts[0], left: parts[1] };
        case 3: return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[1] };
        case 4: return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] };
        default: return EMPTY_MARGINS;
    }
}

function hasAnyMargin(m: MarginValues): boolean {
    return m.top !== null || m.right !== null || m.bottom !== null || m.left !== null;
}

function mergeMargins(base: MarginValues, override: MarginValues): MarginValues {
    return {
        top: override.top ?? base.top,
        right: override.right ?? base.right,
        bottom: override.bottom ?? base.bottom,
        left: override.left ?? base.left,
    };
}

function parseLengthToPx(value: string | null): number | null {
    if (!value) return null;
    const s = value.toLowerCase().trim();
    if (s === '0') return 0;
    const m = s.match(/^([\d.]+)(mm|cm|in|px|pt|pc)$/);
    if (!m) return null;
    const v = parseFloat(m[1]);
    switch (m[2]) {
        case 'px': return v;
        case 'mm': return v * MM_TO_CSS_PX;
        case 'cm': return v * 10 * MM_TO_CSS_PX;
        case 'in': return v * 25.4 * MM_TO_CSS_PX;
        case 'pt': return v * 25.4 / 72 * MM_TO_CSS_PX;
        case 'pc': return v * 25.4 / 6 * MM_TO_CSS_PX;
        default: return null;
    }
}

export async function initCssomCacheAsync(): Promise<PrintCssomData> {
    const pageNameToSize: { [name: string]: string } = {};
    const classToPageName: { [sel: string]: string } = {};
    const pageNameToMargins: { [name: string]: MarginValues } = {};
    const selectorToMargins: { [sel: string]: MarginValues } = {};

    for (const sheet of Array.from(document.styleSheets)) {
        let rules: CSSRuleList | null = null;
        try {
            rules = sheet.cssRules;
        } catch (err: any) {
            // Cross-origin stylesheets can throw when reading cssRules. Same-origin protected sheets
            // can still be fetched with credentials; known CDNs are skipped because they do not carry
            // Servitly @page / report layout rules.
            if (sheet.href && !isKnownCdnUrl(sheet.href)) {
                try {
                    const text = await fetch(sheet.href, { credentials: 'include' }).then(r => r.text());
                    const inMemory = new CSSStyleSheet();
                    await (inMemory as any).replace(text);
                    rules = inMemory.cssRules;
                } catch (_fetchErr: any) { /* unreadable stylesheet: ignore */ }
            }
        }
        if (rules) collectPrintCssRules(rules, pageNameToSize, classToPageName, pageNameToMargins, selectorToMargins);
    }

    const declarations = buildPageDeclarations(pageNameToSize, classToPageName, pageNameToMargins, selectorToMargins);
    const portrait = declarations.find(p => p.orientation === 'portrait') ?? declarations[0];
    const landscape = declarations.find(p => p.orientation === 'landscape') ?? portrait;
    return {
        portraitWidthPx: resolveEffectivePrintWidthPx(portrait),
        landscapeWidthPx: resolveEffectivePrintWidthPx(landscape),
    };
}

function collectPrintCssRules(
    rules: CSSRuleList,
    pageNameToSize: { [name: string]: string },
    classToPageName: { [sel: string]: string },
    pageNameToMargins: { [name: string]: MarginValues },
    selectorToMargins: { [sel: string]: MarginValues }
): void {
    Array.from(rules).forEach(rule => {
        if (!(rule instanceof CSSMediaRule)) return;
        if (!rule.media.mediaText.includes('print')) return;
        Array.from(rule.cssRules).forEach(inner => {
            if (inner instanceof CSSPageRule) {
                const name = inner.selectorText || '';
                const size = (inner.style as any).size || '';
                pageNameToSize[name] = size;
                try {
                    const margins = parseMarginFromStyle(inner.style);
                    if (hasAnyMargin(margins)) {
                        pageNameToMargins[name] = pageNameToMargins[name]
                            ? mergeMargins(pageNameToMargins[name], margins)
                            : margins;
                    }
                } catch (_) { /* skip unparseable margin */ }
            } else if (inner instanceof CSSStyleRule) {
                const sel = inner.selectorText;
                if (sel !== '.landscape-page' && sel !== '.portrait-page') return;
                const pageProp = inner.style.getPropertyValue('page').trim();
                if (pageProp) {
                    classToPageName[sel] = pageProp;
                    try {
                        const margins = parseMarginFromStyle(inner.style);
                        if (hasAnyMargin(margins)) {
                            selectorToMargins[sel] = selectorToMargins[sel]
                                ? mergeMargins(selectorToMargins[sel], margins)
                                : margins;
                        }
                    } catch (_) { /* skip unparseable margin */ }
                }
            }
        });
    });
}

function buildPageDeclarations(
    pageNameToSize: { [name: string]: string },
    classToPageName: { [sel: string]: string },
    pageNameToMargins: { [name: string]: MarginValues },
    selectorToMargins: { [sel: string]: MarginValues }
): PageDeclaration[] {
    const pageNames = new Set<string>(Object.keys(pageNameToSize));
    Object.values(classToPageName).forEach(v => {
        pageNames.add(v === 'auto' ? '' : v);
    });

    // Margins can come from general @page, named @page, and selectors assigned to that page.
    // Later declarations override only the sides they define, mirroring the CSS cascade.
    const generalPageMargins = pageNameToMargins[''] ?? EMPTY_MARGINS;

    const declarations: PageDeclaration[] = [];
    pageNames.forEach(pageName => {
        const sizeString = pageNameToSize[pageName] ?? '';
        const { widthMm, heightMm } = parsePageSize(sizeString);
        const assignedSelectors = Object.entries(classToPageName)
            .filter(([, v]) => (v === 'auto' ? '' : v) === pageName)
            .map(([k]) => k);

        const rawPageMargins = pageNameToMargins[pageName] ?? EMPTY_MARGINS;
        const pageMargins = pageName ? mergeMargins(generalPageMargins, rawPageMargins) : generalPageMargins;

        let containerMargins: MarginValues = EMPTY_MARGINS;
        for (const selector of assignedSelectors) {
            const sm = selectorToMargins[selector];
            if (sm) containerMargins = mergeMargins(containerMargins, sm);
        }

        const hasExplicitMargins = hasAnyMargin(pageMargins) || hasAnyMargin(containerMargins);

        declarations.push({
            pageName,
            sizeString,
            widthMm,
            heightMm,
            orientation: widthMm > heightMm ? 'landscape' : 'portrait',
            pageMargins,
            containerMargins,
            hasExplicitMargins
        });
    });

    if (declarations.length === 0) {
        declarations.push({
            pageName: '', sizeString: 'auto', widthMm: 210, heightMm: 297, orientation: 'portrait',
            pageMargins: EMPTY_MARGINS, containerMargins: EMPTY_MARGINS, hasExplicitMargins: false
        });
    }
    return declarations;
}

const PAGE_KEYWORD_DIMS_MM: { [key: string]: [number, number] } = {
    'a5': [148, 210],
    'a4': [210, 297],
    'a3': [297, 420],
    'b5': [176, 250],
    'b4': [250, 353],
    'jis-b5': [182, 257],
    'jis-b4': [257, 364],
    'letter': [215.9, 279.4],
    'legal': [215.9, 355.6],
    'ledger': [279.4, 431.8],
};

function parsePageSize(sizeStr: string): { widthMm: number; heightMm: number } {
    const s = (sizeStr || '').toLowerCase().trim();
    if (!s || s === 'auto') return { widthMm: 210, heightMm: 297 };

    let baseDims: [number, number] | null = null;
    let orientation: 'portrait' | 'landscape' | null = null;
    const lengths: number[] = [];

    s.split(/\s+/).forEach(token => {
        if (token in PAGE_KEYWORD_DIMS_MM) {
            baseDims = PAGE_KEYWORD_DIMS_MM[token];
        } else if (token === 'landscape') {
            orientation = 'landscape';
        } else if (token === 'portrait') {
            orientation = 'portrait';
        } else {
            const mm = parseLengthToMm(token);
            if (mm !== null) lengths.push(mm);
        }
    });

    if (lengths.length >= 1) {
        baseDims = [lengths[0], lengths[1] ?? lengths[0]];
    }
    if (!baseDims) {
        baseDims = [210, 297];
    }

    const [d1, d2] = baseDims;
    if (orientation === 'landscape') {
        return { widthMm: Math.max(d1, d2), heightMm: Math.min(d1, d2) };
    }
    return { widthMm: Math.min(d1, d2), heightMm: Math.max(d1, d2) };
}

function parseLengthToMm(token: string): number | null {
    const m = token.match(/^([\d.]+)(mm|cm|in|px|pt|pc)$/);
    if (!m) return null;
    const v = parseFloat(m[1]);
    switch (m[2]) {
        case 'mm': return v;
        case 'cm': return v * 10;
        case 'in': return v * 25.4;
        case 'px': return v * 25.4 / 96;
        case 'pt': return v * 25.4 / 72;
        case 'pc': return v * 25.4 / 6;
        default: return null;
    }
}

function isKnownCdnUrl(href: string): boolean {
    const CDN_HOSTS = [
        'cdnjs.cloudflare.com', 'fonts.googleapis.com', 'fonts.gstatic.com',
        'cdn.datatables.net', 'cdn.amcharts.com', 'www.amcharts.com'
    ];
    try {
        const host = new URL(href).hostname;
        return CDN_HOSTS.some(cdn => host === cdn || host.endsWith('.' + cdn));
    } catch (_) {
        return false;
    }
}
